import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../../components/feature/SEOHead';
import ExercicioGratisNavbar from '../components/ExercicioGratisNavbar';
import LeadCaptureForm from '../components/LeadCaptureForm';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const swishSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Técnica Swish PNL Grátis | Reprogramação Mental — Carta do Eu Futuro',
    url: `${SITE_URL}/exercicio-gratis/tecnica-swish-pnl`,
    description: 'Experimente gratuitamente a Técnica Swish de PNL. Reprograme respostas automáticas e substitua padrões mentais limitantes por comportamentos de alta performance.',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Exercícios Grátis', item: `${SITE_URL}/exercicio-gratis/tecnica-swish-pnl` },
        { '@type': 'ListItem', position: 3, name: 'Técnica Swish PNL', item: `${SITE_URL}/exercicio-gratis/tecnica-swish-pnl` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: 'Técnica Swish — PNL',
    description: 'Aprenda a Técnica Swish de Programação Neurolinguística para reprogramar padrões mentais indesejados de forma rápida e eficaz.',
    provider: { '@type': 'Organization', name: 'Carta do Eu Futuro', url: SITE_URL },
    isAccessibleForFree: true,
    educationalLevel: 'Beginner',
  },
];

export default function TecnicaSwishGratis() {
  const [repeticoes, setRepeticoes] = useState(0);
  const [comportamento, setComportamento] = useState('');
  const [gatilho, setGatilho] = useState('');

  const handleSwish = () => {
    if (repeticoes < 3) setRepeticoes(repeticoes + 1);
  };

  const handleReset = () => setRepeticoes(0);

  return (
    <div className="min-h-screen bg-cream-50">
      <SEOHead
        title="Técnica Swish PNL Grátis | Reprogramação Mental — Carta do Eu Futuro"
        description="Experimente gratuitamente a Técnica Swish de PNL. Reprograme respostas automáticas e substitua padrões mentais limitantes."
        keywords="técnica swish, PNL grátis, reprogramação mental, programação neurolinguística"
        canonical="/exercicio-gratis/tecnica-swish-pnl"
        schema={swishSchema}
      />
      <ExercicioGratisNavbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-6 bg-gradient-to-br from-charcoal-950 via-charcoal-900 to-charcoal-950 text-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gold-500/8 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-gold-400/6 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="inline-block px-4 py-2 bg-gold-500/15 border border-gold-500/30 text-gold-400 rounded-full text-sm font-semibold mb-6 tracking-wide font-['Montserrat']">
            VERSÃO GRATUITA
          </div>
          <h1 className="font-['Playfair_Display'] text-5xl font-bold mb-6 leading-tight text-white">
            Técnica Swish (Switch) — PNL
          </h1>
          <p className="text-xl text-white/70 mb-8 leading-relaxed font-['Montserrat'] font-light">
            Reprogramação rápida de respostas automáticas. Experimente uma versão simplificada desta técnica clássica de PNL.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/8 backdrop-blur-sm rounded-xl p-4 border border-gold-500/20">
              <div className="w-10 h-10 flex items-center justify-center mb-2"><i className="ri-flashlight-line text-gold-400 text-2xl"></i></div>
              <div className="font-semibold mb-1 text-white font-['Playfair_Display']">Rápido</div>
              <div className="text-sm text-white/55 font-['Montserrat']">Resultados em minutos</div>
            </div>
            <div className="bg-white/8 backdrop-blur-sm rounded-xl p-4 border border-gold-500/20">
              <div className="w-10 h-10 flex items-center justify-center mb-2"><i className="ri-focus-3-line text-gold-400 text-2xl"></i></div>
              <div className="font-semibold mb-1 text-white font-['Playfair_Display']">Prático</div>
              <div className="text-sm text-white/55 font-['Montserrat']">Fácil de aplicar</div>
            </div>
            <div className="bg-white/8 backdrop-blur-sm rounded-xl p-4 border border-gold-500/20">
              <div className="w-10 h-10 flex items-center justify-center mb-2"><i className="ri-refresh-line text-gold-400 text-2xl"></i></div>
              <div className="font-semibold mb-1 text-white font-['Playfair_Display']">Automático</div>
              <div className="text-sm text-white/55 font-['Montserrat']">Reprograma o subconsciente</div>
            </div>
          </div>
        </div>
      </section>

      {/* O que é */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-['Playfair_Display'] text-3xl font-bold text-charcoal-800 mb-6">O que é a Técnica Swish?</h2>
          <p className="text-lg text-charcoal-600 leading-relaxed mb-6 font-['Montserrat'] font-light">Uma técnica de Programação Neurolinguística que substitui automaticamente uma imagem mental indesejada por outra fortalecedora.</p>
          <p className="text-lg text-charcoal-600 leading-relaxed font-['Montserrat'] font-light">Ela atua no nível subconsciente, alterando o &quot;gatilho interno&quot; que leva a um comportamento ou emoção. Em vez de lutar contra o padrão, você instala um novo.</p>
        </div>
      </section>

      {/* Passos */}
      <section className="py-16 px-6 bg-cream-100/60">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-['Playfair_Display'] text-3xl font-bold text-charcoal-800 mb-8">Passos da Técnica</h2>
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8 border border-gold-200/40">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-charcoal-800 to-charcoal-900 flex items-center justify-center text-gold-400 font-bold text-xl flex-shrink-0">1</div>
                <div className="flex-1">
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-charcoal-800 mb-3">Identifique o comportamento ou emoção</h3>
                  <p className="text-charcoal-600 mb-4 font-['Montserrat'] font-light">Escolha algo específico que você quer mudar.</p>
                  <p className="text-sm text-charcoal-500 italic font-['Montserrat']">Exemplos: &quot;Fico ansiosa antes de reuniões&quot;, &quot;Procrastino quando preciso trabalhar&quot;</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gold-200/40">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-charcoal-800 to-charcoal-900 flex items-center justify-center text-gold-400 font-bold text-xl flex-shrink-0">2</div>
                <div className="flex-1">
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-charcoal-800 mb-3">Descubra o gatilho interno</h3>
                  <p className="text-charcoal-600 mb-4 font-['Montserrat'] font-light">O que passa pela sua mente imediatamente antes disso acontecer?</p>
                  <p className="text-sm text-charcoal-500 font-['Montserrat']">Geralmente é uma imagem, uma cena, uma sensação corporal ou um pensamento visual.</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border border-gold-200/40">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-charcoal-800 to-charcoal-900 flex items-center justify-center text-gold-400 font-bold text-xl flex-shrink-0">3</div>
                <div className="flex-1">
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-charcoal-800 mb-3">Crie a imagem do &quot;EU IDEAL&quot;</h3>
                  <p className="text-charcoal-600 mb-4 font-['Montserrat'] font-light">Imagine como você gostaria de reagir na mesma situação.</p>
                  <p className="text-sm text-charcoal-500 font-['Montserrat']">Inclua: postura corporal, expressão facial, energia emocional, segurança e naturalidade.</p>
                </div>
              </div>
            </div>
            {/* Passos Bloqueados */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cream-50/80 to-cream-50 z-10 flex items-center justify-center backdrop-blur-sm rounded-2xl">
                <div className="text-center p-8">
                  <div className="w-16 h-16 rounded-full bg-charcoal-100 flex items-center justify-center mx-auto mb-4"><i className="ri-lock-fill text-3xl text-charcoal-400"></i></div>
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold text-charcoal-800 mb-2">Mais 5 passos na versão completa</h3>
                  <p className="text-charcoal-500 mb-6 font-['Montserrat']">Desbloqueie o protocolo completo com todos os detalhes e técnicas avançadas</p>
                  <Link to="/planos#exercicios-pnl" className="inline-block px-8 py-3 bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 rounded-full font-semibold hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap font-['Montserrat']">Ver Versão Completa</Link>
                </div>
              </div>
              <div className="blur-sm pointer-events-none">
                <div className="bg-white rounded-2xl p-8 border border-gold-200/40 mb-6">
                  <div className="flex items-start gap-4"><div className="w-12 h-12 rounded-full bg-charcoal-200 flex items-center justify-center text-charcoal-400 font-bold text-xl flex-shrink-0">4</div><div className="flex-1"><h3 className="font-['Playfair_Display'] text-xl font-bold text-charcoal-800 mb-3">Ajuste as qualidades das imagens</h3><p className="text-charcoal-600">Configure tamanho, nitidez e proximidade...</p></div></div>
                </div>
                <div className="bg-white rounded-2xl p-8 border border-gold-200/40">
                  <div className="flex items-start gap-4"><div className="w-12 h-12 rounded-full bg-charcoal-200 flex items-center justify-center text-charcoal-400 font-bold text-xl flex-shrink-0">5</div><div className="flex-1"><h3 className="font-['Playfair_Display'] text-xl font-bold text-charcoal-800 mb-3">Execute o movimento SWISH</h3><p className="text-charcoal-600">O momento da transformação...</p></div></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mini Prática */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-cream-100 rounded-3xl p-8 border border-gold-200/50">
            <h2 className="font-['Playfair_Display'] text-3xl font-bold text-charcoal-800 mb-6">Mini Prática Gratuita</h2>
            <p className="text-charcoal-600 mb-8 font-['Montserrat'] font-light">Experimente uma versão simplificada da técnica (limitado a 3 repetições)</p>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-charcoal-700 mb-2 font-['Montserrat']">1. Qual comportamento você quer mudar?</label>
                <textarea value={comportamento} onChange={(e) => setComportamento(e.target.value)} maxLength={200} placeholder="Ex: Fico ansioso antes de apresentações..." className="w-full px-4 py-3 rounded-xl border border-gold-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-200 outline-none transition-all resize-none font-['Montserrat'] text-sm text-charcoal-700" rows={3} />
                <div className="text-xs text-charcoal-400 mt-1 font-['Montserrat']">{comportamento.length}/200 caracteres</div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-charcoal-700 mb-2 font-['Montserrat']">2. Qual é o gatilho interno (imagem mental)?</label>
                <textarea value={gatilho} onChange={(e) => setGatilho(e.target.value)} maxLength={200} placeholder="Ex: Vejo as pessoas me julgando..." className="w-full px-4 py-3 rounded-xl border border-gold-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-200 outline-none transition-all resize-none font-['Montserrat'] text-sm text-charcoal-700" rows={3} />
                <div className="text-xs text-charcoal-400 mt-1 font-['Montserrat']">{gatilho.length}/200 caracteres</div>
              </div>
              <div className="bg-white rounded-xl p-6 border border-gold-200/50">
                <h3 className="font-['Playfair_Display'] text-lg font-bold text-charcoal-800 mb-4">Movimento SWISH Simplificado</h3>
                <p className="text-sm text-charcoal-500 mb-4 font-['Montserrat']">Visualize a imagem indesejada sendo rapidamente substituída pela imagem do seu EU IDEAL</p>
                <div className="text-center mb-6">
                  <div className="inline-block px-6 py-3 bg-cream-100 rounded-full border border-gold-300/40">
                    <span className="font-['Playfair_Display'] text-3xl font-bold text-charcoal-800">{repeticoes}/3</span>
                    <span className="text-sm text-charcoal-500 ml-2 font-['Montserrat']">repetições</span>
                  </div>
                </div>
                <div className="flex gap-3 justify-center">
                  <button onClick={handleSwish} disabled={repeticoes >= 3} className={`px-8 py-3 rounded-full font-semibold transition-all whitespace-nowrap font-['Montserrat'] cursor-pointer ${repeticoes >= 3 ? 'bg-charcoal-100 text-charcoal-400 cursor-not-allowed' : 'bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 hover:from-gold-600 hover:to-gold-800'}`}>{repeticoes >= 3 ? 'Limite Atingido' : 'SWISH!'}</button>
                  <button onClick={handleReset} className="px-6 py-3 rounded-full font-semibold bg-charcoal-100 text-charcoal-700 hover:bg-charcoal-200 transition-all whitespace-nowrap font-['Montserrat'] cursor-pointer"><i className="ri-restart-line mr-2"></i>Resetar</button>
                </div>
                {repeticoes >= 3 && (
                  <div className="mt-6 p-4 bg-cream-50 border border-gold-200 rounded-xl">
                    <p className="text-sm text-charcoal-600 text-center font-['Montserrat']"><i className="ri-lock-fill mr-2 text-gold-500"></i><strong>Limite da versão gratuita atingido!</strong> Na versão completa você pode fazer 10+ repetições e acessar técnicas avançadas.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <LeadCaptureForm exercicioNome="Técnica Swish (PNL)" />

      {/* CTA Final */}
      <section className="py-16 px-6 bg-gradient-to-br from-charcoal-950 via-charcoal-900 to-charcoal-950 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-16 h-px bg-gold-500/40 mx-auto mb-8"></div>
          <h2 className="font-['Playfair_Display'] text-4xl font-bold mb-6 text-white">Pronto para transformar seus padrões mentais?</h2>
          <p className="text-xl text-white/60 mb-8 font-['Montserrat'] font-light">Acesse a versão completa da Técnica Swish e reprograme seu subconsciente de forma rápida e eficaz.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/planos#exercicios-pnl" className="px-10 py-4 bg-gradient-to-r from-gold-500 to-gold-700 text-charcoal-900 rounded-full font-bold text-sm hover:from-gold-600 hover:to-gold-800 transition-all whitespace-nowrap font-['Montserrat'] tracking-wide">Comprar por R$ 18</Link>
            <Link to="/planos#exercicios-pnl" className="px-10 py-4 bg-white/8 text-white rounded-full font-bold text-sm hover:bg-white/15 transition-all whitespace-nowrap border border-gold-500/20 font-['Montserrat'] tracking-wide">Pacote 6 Técnicas - R$ 47</Link>
          </div>
          <p className="text-sm text-white/40 mt-6 font-['Montserrat']">Acesso vitalício • Auto-save • Sistema de conquistas</p>
        </div>
      </section>
    </div>
  );
}
